import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, CardHeader, Typography, CircularProgress, Alert, LinearProgress, List, ListItem,
  ListItemButton, ListItemText, IconButton, Tooltip, Chip, Divider, Stack, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Business as CompaniesIcon, CheckCircle as ActiveIcon, HourglassTop as TrialIcon, Paid as PaidIcon, NewReleases as NewIcon,
  People as UsersIcon, Security as DetectionsIcon, FolderOpen as CasesIcon, Refresh as RefreshIcon,
  AutoAwesome as AiIcon, SupportAgent as SupportIcon, Backup as BackupIcon, CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, PlanChip, errorMessage } from '../../components/Admin/adminUi';

const KpiTile = ({ label, value, icon, color = 'primary.main', onClick }) => (
  <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ color, display: 'flex', '& svg': { fontSize: 36 } }}>{icon}</Box>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>{Number(value || 0).toLocaleString()}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const daysUntil = (date) => Math.ceil((new Date(date).getTime() - Date.now()) / 86400000);

/** Most recent N entries, newest first, from an array of ISO timestamps or date-bearing paths. */
const recentFirst = (arr, n = 3) => [...(arr || [])].sort().reverse().slice(0, n);

const AdminOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clearingCooldown, setClearingCooldown] = useState(false);
  const [clearResult, setClearResult] = useState(null);
  const [backups, setBackups] = useState(null);
  const [backupsLoading, setBackupsLoading] = useState(true);
  const [backupsError, setBackupsError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.admin.getOverview();
      setData(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load overview'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBackups = useCallback(async () => {
    try {
      setBackupsLoading(true);
      setBackupsError(null);
      const res = await apiService.admin.getBackups();
      setBackups(res.data);
    } catch (err) {
      setBackupsError(errorMessage(err, 'Failed to load backup status'));
    } finally {
      setBackupsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBackups(); }, [loadBackups]);

  const handleExportNow = async () => {
    try {
      setExporting(true);
      setExportResult(null);
      const res = await apiService.admin.runBackupExport();
      const { firestore, auth, errors } = res.data || {};
      const parts = [];
      if (firestore) parts.push('Firestore export started');
      if (auth) parts.push(`Auth users exported: ${auth.count ?? 0}`);
      if (!parts.length && (!errors || !errors.length)) parts.push('Export request accepted');
      setExportResult({ ok: !(errors && errors.length), message: parts.join(' · '), errors: errors || [] });
      await loadBackups();
    } catch (err) {
      setExportResult({ ok: false, message: errorMessage(err, 'Failed to run backup export'), errors: [] });
    } finally {
      setExporting(false);
    }
  };

  const handleClearCooldown = async () => {
    try {
      setClearingCooldown(true);
      setClearResult(null);
      const res = await apiService.admin.clearAiCooldown();
      setClearResult(res.data?.probe || null);
      await load();
    } catch (err) {
      setClearResult({ available: false, error: errorMessage(err, 'Failed to clear cooldown') });
    } finally {
      setClearingCooldown(false);
    }
  };

  if (loading && !data) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  const d = data || {};
  const byPlan = d.byPlan || [];
  const total = d.companies || 0;
  const expiring = [...(d.expiringTrials || [])].sort((a, b) => new Date(a.trialEndsAt) - new Date(b.trialEndsAt));
  const ai = d.ai || {};
  const aiLimits = ai.limits || {};
  const aiMinute = ai.minute || {};
  const aiDay = ai.day || {};
  const aiStats = ai.stats || {};
  const aiCooldown = ai.cooldown || null;
  const minutePct = aiLimits.rpm ? Math.min(100, Math.round(((aiMinute.used || 0) / aiLimits.rpm) * 100)) : 0;
  const dayPct = aiLimits.rpd ? Math.min(100, Math.round(((aiDay.used || 0) / aiLimits.rpd) * 100)) : 0;

  const b = backups || {};
  const pitrEnabled = b.pitr === 'POINT_IN_TIME_RECOVERY_ENABLED';
  const schedules = b.schedules || [];
  const latestBackups = b.backups || [];
  const bExports = b.exports || {};
  const recentFirestore = recentFirst(bExports.firestore);
  const recentAuth = recentFirst(bExports.auth);
  const recentTenants = recentFirst(bExports.tenants);
  const recentRetention = recentFirst(bExports.retention);

  return (
    <Box>
      <AdminPageHeader
        title="Overview"
        subtitle="Platform-wide health across all customer tenants"
        actions={<Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>}
      />
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Companies" value={d.companies} icon={<CompaniesIcon />} onClick={() => navigate('/admin/customers')} /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Active" value={d.activeCompanies} icon={<ActiveIcon />} color="success.main" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Trials" value={d.trials} icon={<TrialIcon />} color="warning.main" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Paid" value={d.paid} icon={<PaidIcon />} color="secondary.main" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="New (last 30 days)" value={d.newLast30d} icon={<NewIcon />} color="info.main" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Users" value={d.users} icon={<UsersIcon />} /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Detections" value={d.detections} icon={<DetectionsIcon />} color="error.main" /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Cases" value={d.cases} icon={<CasesIcon />} /></Grid>
        <Grid item xs={12} sm={6} md={3}><KpiTile label="Open support tickets" value={d.support?.open} icon={<SupportIcon />} color={d.support?.open ? 'warning.main' : 'success.main'} onClick={() => navigate('/admin/support')} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Customers by plan" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              {byPlan.length === 0 && <Typography color="text.secondary">No data</Typography>}
              {byPlan.map((row) => {
                const pct = total ? Math.round((row.count / total) * 100) : 0;
                return (
                  <Box key={row.plan} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <PlanChip plan={row.plan} />
                      <Typography variant="body2"><strong>{row.count}</strong> <Typography component="span" variant="caption" color="text.secondary">({pct}%)</Typography></Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }} />
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Trials expiring within 7 days"
              titleTypographyProps={{ variant: 'h6' }}
              action={<Chip label={expiring.length} size="small" color={expiring.length ? 'warning' : 'default'} sx={{ mt: 1, mr: 1 }} />}
            />
            <Divider />
            {expiring.length === 0 ? (
              <CardContent><Typography color="text.secondary">No trials expiring soon.</Typography></CardContent>
            ) : (
              <List dense disablePadding>
                {expiring.map((t) => {
                  const days = daysUntil(t.trialEndsAt);
                  const label = days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Expires today' : `${days}d left`;
                  return (
                    <ListItem key={t.id} disablePadding secondaryAction={<Chip size="small" label={label} color={days <= 0 ? 'error' : days <= 3 ? 'warning' : 'default'} />}>
                      <ListItemButton onClick={() => navigate(`/admin/customers/${t.id}`)}>
                        <ListItemText primary={t.name} secondary={`Trial ends ${formatDate(t.trialEndsAt)}`} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<AiIcon color="primary" />}
              title="AI usage"
              titleTypographyProps={{ variant: 'h6' }}
              subheader="Google AI request budget and limiter status"
            />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>This minute</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{aiMinute.used ?? 0} / {aiLimits.rpm ?? '-'} requests</Typography>
                  <LinearProgress variant="determinate" value={minutePct} sx={{ height: 8, borderRadius: 4 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Today</Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{aiDay.used ?? 0} / {aiLimits.rpd ?? '-'} requests</Typography>
                  <LinearProgress variant="determinate" value={dayPct} sx={{ height: 8, borderRadius: 4 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Concurrency</Typography>
                  <Typography variant="h6">{ai.inFlight ?? 0} / {aiLimits.concurrency ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">in flight</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Queued</Typography>
                  <Typography variant="h6">{ai.queued ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">background share {aiLimits.backgroundShare ?? '-'}</Typography>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                <Chip size="small" label={`Calls: ${aiStats.calls ?? 0}`} />
                <Chip size="small" color="success" label={`OK: ${aiStats.ok ?? 0}`} />
                <Chip size="small" color="warning" label={`Rate-limited: ${aiStats.rateLimited ?? 0}`} />
                <Chip size="small" color="info" label={`Deferred: ${aiStats.deferred ?? 0}`} />
                <Chip size="small" color="error" label={`Failures: ${aiStats.failures ?? 0}`} />
              </Stack>

              {aiStats.lastError && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Last error: {aiStats.lastError}{aiStats.lastErrorAt ? ` (${formatDate(aiStats.lastErrorAt)})` : ''}
                </Typography>
              )}

              {aiCooldown && (
                <Alert severity={aiCooldown.reason === 'credits_depleted' ? 'error' : 'warning'} sx={{ mt: 2 }}>
                  {aiCooldown.reason === 'credits_depleted'
                    ? 'Google AI prepaid credits are depleted — top up at https://ai.studio/projects'
                    : `AI requests are in cooldown (${aiCooldown.reason || 'rate limited'})${aiCooldown.secondsLeft != null ? ` — ${aiCooldown.secondsLeft}s left` : ''}.`}
                </Alert>
              )}

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={clearingCooldown ? <CircularProgress size={14} /> : <RefreshIcon />}
                  disabled={clearingCooldown}
                  onClick={handleClearCooldown}
                >
                  Clear cooldown & re-check
                </Button>
                {clearResult && (
                  <Typography variant="body2" color={clearResult.available ? 'success.main' : 'error.main'}>
                    {clearResult.available ? 'AI is now available.' : (clearResult.error || 'AI is still unavailable.')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<BackupIcon color="primary" />}
              title="Backups & archive"
              titleTypographyProps={{ variant: 'h6' }}
              subheader="Firestore backup schedules, point-in-time recovery and exported archives"
              action={(
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <CloudDownloadIcon />}
                  disabled={exporting || backupsLoading}
                  onClick={handleExportNow}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Export now
                </Button>
              )}
            />
            <Divider />
            <CardContent>
              {backupsLoading && !backups ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={28} /></Box>
              ) : (
                <>
                  {backupsError && <Alert severity="error" sx={{ mb: 2 }}>{backupsError}</Alert>}
                  {b.error && <Alert severity="warning" sx={{ mb: 2 }}>{b.error}</Alert>}

                  {exportResult && (
                    <Alert severity={exportResult.ok ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setExportResult(null)}>
                      {exportResult.message}
                      {exportResult.errors.length > 0 && (
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {exportResult.errors.map((e, i) => <li key={i}><Typography variant="body2">{typeof e === 'string' ? e : JSON.stringify(e)}</Typography></li>)}
                        </Box>
                      )}
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} alignItems="center">
                    <Chip
                      size="small"
                      label={pitrEnabled ? 'Point-in-time recovery: enabled' : 'Point-in-time recovery: disabled'}
                      color={pitrEnabled ? 'success' : 'error'}
                    />
                    <Chip
                      size="small"
                      label={b.deleteProtection ? 'Delete protection: enabled' : 'Delete protection: disabled'}
                      color={b.deleteProtection ? 'success' : 'error'}
                    />
                    {b.bucket && (
                      <Typography variant="caption" color="text.secondary">
                        Bucket: <Box component="code" sx={{ fontFamily: 'monospace' }}>{b.bucket}</Box>
                      </Typography>
                    )}
                    {b.database && (
                      <Typography variant="caption" color="text.secondary">
                        Database: <Box component="code" sx={{ fontFamily: 'monospace' }}>{b.database}</Box>
                      </Typography>
                    )}
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Backup schedules</Typography>
                      {schedules.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No schedules configured.</Typography>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Recurrence</TableCell>
                                <TableCell align="right">Retention (days)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {schedules.map((s, i) => (
                                <TableRow key={s.name || i}>
                                  <TableCell>{s.name}</TableCell>
                                  <TableCell>{s.recurrence}</TableCell>
                                  <TableCell align="right">{s.retentionDays ?? '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}

                      <Typography variant="subtitle2" sx={{ mt: 3 }} gutterBottom>Latest backups</Typography>
                      {latestBackups.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No backups yet.</Typography>
                      ) : (
                        <List dense disablePadding>
                          {latestBackups.map((bk, i) => (
                            <ListItem key={bk.name || i} disablePadding sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={`${formatDate(bk.snapshotTime)} — ${bk.state || 'UNKNOWN'}`}
                                secondary={bk.expireTime ? `Expires ${formatDate(bk.expireTime)}` : undefined}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Exports</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Firestore exports</Typography>
                          {recentFirestore.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">None yet</Typography>
                          ) : (
                            <List dense disablePadding>
                              {recentFirestore.map((ts, i) => (
                                <ListItem key={i} disablePadding><ListItemText primary={formatDateTime(ts)} /></ListItem>
                              ))}
                            </List>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Auth exports</Typography>
                          {recentAuth.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">None yet</Typography>
                          ) : (
                            <List dense disablePadding>
                              {recentAuth.map((ts, i) => (
                                <ListItem key={i} disablePadding><ListItemText primary={formatDateTime(ts)} /></ListItem>
                              ))}
                            </List>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Tenant exports</Typography>
                          {recentTenants.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">None yet</Typography>
                          ) : (
                            <List dense disablePadding>
                              {recentTenants.map((p, i) => (
                                <ListItem key={i} disablePadding>
                                  <ListItemText primary={<Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p}</Box>} />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Retention archives</Typography>
                          {recentRetention.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">None yet</Typography>
                          ) : (
                            <List dense disablePadding>
                              {recentRetention.map((p, i) => (
                                <ListItem key={i} disablePadding>
                                  <ListItemText primary={<Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p}</Box>} />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminOverview;
